import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import constate from "constate";
import { useCallback } from "react";

export type ThemeMode = "light" | "dark";

interface UserPreferencesState {
  themeMode: ThemeMode;
  setThemeMode: (next: ThemeMode | ((previous: ThemeMode) => ThemeMode)) => void;
  toggleThemeMode: () => void;
}

const THEME_MODE_STORAGE_KEY = "cbh-admin:themeMode";

const NOOP = () => {
  // Toggle is disabled — setters are no-ops so accidental callers can't flip
  // the theme out from under a flag-off render.
};

// Stable reference returned when the toggle flag is off, so consumers don't
// re-render every time the provider does.
const LIGHT_USER_PREFERENCES: UserPreferencesState = {
  themeMode: "light",
  setThemeMode: NOOP,
  toggleThemeMode: NOOP,
};

function getSystemThemeMode(): ThemeMode {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function useUserPreferencesState(): UserPreferencesState {
  const isThemeModeToggleEnabled = useCbhFlag(CbhFeatureFlag.LIGHT_DARK_MODE_TOGGLE, {
    defaultValue: false,
  });

  // The localStorage hook must be called unconditionally to keep hook order
  // stable across flag flips. When the flag is off the value is read but
  // ignored — the provider returns a constant "light" instead.
  const [themeMode, setThemeMode] = useLocalStorage<ThemeMode>(
    THEME_MODE_STORAGE_KEY,
    getSystemThemeMode
  );

  const toggleThemeMode = useCallback(() => {
    setThemeMode((current) => (current === "dark" ? "light" : "dark"));
  }, [setThemeMode]);

  if (!isThemeModeToggleEnabled) {
    return LIGHT_USER_PREFERENCES;
  }

  return { themeMode, setThemeMode, toggleThemeMode };
}

export const [UserPreferencesProvider, useUserPreferences] = constate(useUserPreferencesState);

export function useThemeMode() {
  return useUserPreferences();
}
